// Copyright (c) StrangeBee 2024

import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import { useLocalMessage } from '@Helpers/locale';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { THForm } from '@Molecules/THForm/THForm';
import { FormInstance, Space } from 'antd';
import { FormListFieldData } from 'antd/lib/form/FormList';
import React, { ReactElement, useState } from 'react';
import { InputShare, OrganisationOutput, ProfileOutput } from 'thehive-sdk';

import './CaseCreateSharingManage.scss';

interface CaseCreateSharingManageProps {
    form: FormInstance;
    orgList: OrganisationOutput[];
    orgProfileList: ProfileOutput[];
    triggerElem?: ReactElement;
    fields: FormListFieldData[];
    visible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
}

export const CaseCreateSharingManage = ({
    form,
    orgList,
    orgProfileList,
    triggerElem,
    fields,
    visible,
    onVisibleChange,
}: CaseCreateSharingManageProps): ReactElement => {
    const i = useLocalMessage();

    const [filter, setFilter] = useState('');

    return (
        <THDrawer
            visible={visible}
            afterVisibleChange={(visible) => {
                onVisibleChange?.(visible);
            }}
            title={i('case.create.form.sharing.title')}
            triggerElem={triggerElem}
            className="drawer-form"
        >
            {(toggleDrawer) => (
                <THForm.Layout footer={<FormSubmitButton disabled={false} onSubmit={toggleDrawer} />}>
                    <div className="case-create-sharing-manage">
                        <THInput
                            value={filter}
                            onChange={(event) => setFilter(event.target.value)}
                            placeholder="Enter an organisation name"
                        />

                        {fields
                            .filter(({ name }) => {
                                const field = form.getFieldValue(['sharingParameters', name]) as InputShare & {
                                    editable?: boolean;
                                };

                                return field?.organisation.toLowerCase().includes(filter.trim().toLowerCase());
                            })
                            .map(({ key, name }) => {
                                const field = form.getFieldValue(['sharingParameters', name]) as InputShare & {
                                    editable?: boolean;
                                };

                                const org = orgList.find(({ name, locked }) => field?.organisation === name && !locked);

                                return (
                                    org && (
                                        <div className="case-create-form__sharing__item" key={key}>
                                            <Space>
                                                <UserAvatar avatar={org.avatar} />
                                                {org.name}
                                            </Space>

                                            <div className="case-create-form__sharing__item__options">
                                                <THForm.Item
                                                    name={[name, 'isShared']}
                                                    label={i('case.create.form.sharing.share.label')}
                                                    valuePropName="checked"
                                                >
                                                    <THSwitch />
                                                </THForm.Item>

                                                <THForm.Item<any>
                                                    noStyle
                                                    shouldUpdate={(prev, next) =>
                                                        prev.sharingParameters[name].isShared !==
                                                            next.sharingParameters[name].isShared ||
                                                        prev.sharingParameters[name].taskRule !==
                                                            next.sharingParameters[name].taskRule ||
                                                        prev.sharingParameters[name].observableRule !==
                                                            next.sharingParameters[name].observableRule
                                                    }
                                                >
                                                    {({ getFieldValue }) => (
                                                        <>
                                                            <THForm.Item
                                                                name={[name, 'profile']}
                                                                label={i('case.create.form.sharing.profile.label')}
                                                            >
                                                                <THSelect
                                                                    options={orgProfileList.map(({ name: value }) => ({
                                                                        label: value,
                                                                        value,
                                                                    }))}
                                                                    disabled={
                                                                        !getFieldValue([
                                                                            'sharingParameters',
                                                                            name,
                                                                            'isShared',
                                                                        ])
                                                                    }
                                                                />
                                                            </THForm.Item>
                                                        </>
                                                    )}
                                                </THForm.Item>
                                            </div>
                                        </div>
                                    )
                                );
                            })}
                    </div>
                </THForm.Layout>
            )}
        </THDrawer>
    );
};
